import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Download, ExternalLink } from "lucide-react";
import { api, API } from "../lib/api";
import { AsOf, FxStamp } from "../components/AsOf";
import { VerifyChip } from "../components/DataLabel";
import { Reveal, useSeo } from "../lib/ui";
import { StageBar, StageClose } from "../components/StageChain";
import { TrackAnchor } from "../components/TrackAnchor";
import { KplAlignment } from "../components/KplAlignment";

const usd = (n) =>
  n >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : n >= 1e3 ? `$${Math.round(n).toLocaleString()}` : `$${Math.round(n || 0)}`;
const ksh = (n) =>
  n >= 1e9 ? `KSh ${(n / 1e9).toFixed(2)}bn` : n >= 1e6 ? `KSh ${(n / 1e6).toFixed(1)}m` : `KSh ${Math.round(n || 0).toLocaleString()}`;

const Bullets = ({ label, items, accent, testId }) =>
  items?.length ? (
    <div data-testid={testId}>
      <p className="overline" style={accent ? { color: accent } : undefined}>
        {label}
      </p>
      <ul className="mt-4 space-y-3">
        {items.map((t) => (
          <li key={t} className="flex gap-3 text-sm leading-relaxed text-zinc-400">
            <span className="mt-[7px] h-1.5 w-1.5 shrink-0" style={{ background: accent || "#E32118" }} />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

const CostTable = ({ step }) => (
  <div data-testid={`step-costs-${step.order}`}>
    <p className="overline text-ochre">What it costs in {step.county}</p>
    <div className="mt-4 border border-white/10">
      {step.cost_lines.map((l) => (
        <div
          key={l.label}
          className="grid gap-2 border-b border-white/5 px-4 py-3 md:grid-cols-[minmax(0,2fr)_110px_110px]"
        >
          <span className="text-sm text-white">{l.label}</span>
          <span className="text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400">{l.kind}</span>
          <span className="mono text-sm text-ochre md:text-right">{usd(l.usd)}</span>
        </div>
      ))}
      {step.cost_lines.some((l) => l.kind === "One-off") ? (
        <div className="grid gap-2 bg-[#140406] px-4 py-3 md:grid-cols-[minmax(0,2fr)_110px_110px]">
          <span className="text-[0.6rem] font-bold uppercase tracking-[0.18em] text-white">
            One-off {usd(step.one_off_usd)} · {ksh(step.one_off_ksh)}
          </span>
          <span className="text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">
            {step.annual_usd ? "Every year" : "No running cost"}
          </span>
          <span className="mono text-sm font-bold text-white md:text-right">
            {step.annual_usd ? usd(step.annual_usd) : "—"}
          </span>
        </div>
      ) : (
        <div className="grid gap-2 bg-[#140406] px-4 py-3 md:grid-cols-[minmax(0,2fr)_110px_110px]">
          <span className="text-[0.6rem] font-bold uppercase tracking-[0.18em] text-white">
            No one-off cost — this step is a running commitment
          </span>
          <span className="text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">Every year</span>
          <span className="mono text-sm font-bold text-white md:text-right">{usd(step.annual_usd)}</span>
        </div>
      )}
    </div>
  </div>
);

const StepBlock = ({ step }) => (
  <Reveal delay={0.05}>
    <div className="grid gap-8 border-t border-zinc-900 py-12 lg:grid-cols-[380px_1fr]" data-testid={`step-row-${step.order}`}>
      <div>
        <span className="font-display text-6xl font-bold leading-none text-zinc-800">
          {String(step.order).padStart(2, "0")}
        </span>
        <h2 className="display-3 mt-5">{step.title}</h2>
        <div className="mt-5 flex flex-wrap items-center gap-3 text-[0.66rem] uppercase tracking-[0.18em]">
          <span className="bg-maasai px-2.5 py-1 text-white">Month {step.months}</span>
          <span className="mono text-sm text-ochre">
            {step.one_off_usd ? `${usd(step.one_off_usd)} to do it` : "No one-off cost"}
          </span>
        </div>
        <p className="mt-5 text-sm leading-relaxed text-zinc-400">{step.purpose}</p>
        {step.kpl_pillar ? (
          <div className="mt-5" data-testid={`step-kpl-${step.order}`}>
            <span className="mono inline-block border border-maasai px-3 py-1.5 text-[0.55rem] font-bold uppercase tracking-[0.16em] text-maasai">
              KPL pillar · {step.kpl_pillar}
            </span>
          </div>
        ) : null}
        {step.uk ? (
          <a
            href={step.uk.url}
            target="_blank"
            rel="noreferrer"
            data-testid={`step-uk-${step.order}`}
            className="mt-6 block border-l-2 border-ochre pl-4 text-sm leading-relaxed text-zinc-400 hover:text-white"
          >
            <span className="block text-[0.58rem] font-bold uppercase tracking-[0.18em] text-ochre">
              How other professional clubs in the Premier League developed <ExternalLink size={10} className="ml-1 inline" />
            </span>
            <span className="mt-2 block">{step.uk.note}</span>
            <span className="mono mt-2 block text-[0.58rem] uppercase tracking-[0.14em] text-zinc-400">
              {step.uk.label}
            </span>
          </a>
        ) : null}
      </div>

      <div className="space-y-8">
        <div className="grid gap-8 md:grid-cols-2">
          <Bullets label="The work" items={step.work} testId={`step-work-${step.order}`} />
          <Bullets label="What you end up with" items={step.outputs} accent="#E5A91E" testId={`step-outputs-${step.order}`} />
        </div>
        <CostTable step={step} />
      </div>
    </div>
  </Reveal>
);

const Phasing = ({ steps }) => (
  <div className="border-t border-white/20" data-testid="steps-phasing">
    {steps.map((s) => (
      <div
        key={s.order}
        className="grid grid-cols-[44px_minmax(0,1fr)] items-start gap-4 border-b border-white/10 py-5 md:grid-cols-[56px_120px_minmax(0,1fr)_130px_130px] md:items-center md:gap-6"
        data-testid={`phase-row-${s.order}`}
      >
        <span className="mono text-sm text-maasai">{String(s.order).padStart(2, "0")}</span>
        <span className="mono hidden text-[0.62rem] uppercase tracking-[0.14em] text-zinc-500 md:block">
          Month {s.months}
        </span>
        <span className="min-w-0">
          <span className="block font-display text-base uppercase leading-tight tracking-tight text-white md:text-lg">
            {s.title}
          </span>
          <span className="mono mt-1 block text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500 md:hidden">
            Month {s.months} · {s.one_off_usd ? `${usd(s.one_off_usd)} one-off` : "no one-off cost"} ·{" "}
            {s.annual_usd ? `${usd(s.annual_usd)} a year` : "no running cost"}
          </span>
        </span>
        <span className="mono hidden text-sm text-ochre md:block md:text-right">
          {s.one_off_usd ? usd(s.one_off_usd) : "—"}
        </span>
        <span className="mono hidden text-sm text-zinc-400 md:block md:text-right">
          {s.annual_usd ? `${usd(s.annual_usd)}/yr` : "—"}
        </span>
      </div>
    ))}
  </div>
);

export default function Plans() {
  const [params, setParams] = useSearchParams();
  const track = params.get("track") || "professional";
  const county = params.get("county") || "Nairobi";
  const view = params.get("view") || "steps";
  const pillar = params.get("pillar") || "all";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "Recommended Steps — KickStartKenyaFootball",
    description:
      "The recommended steps for building football in a Kenyan county, in two tracks: the professional game — incorporation, governance, land, business plan, trademark, club base, licensing and a modular stadium — and the national game, from a county plan to courts, leagues, coaches and a talent pathway. Costed county by county.",
  });

  useEffect(() => {
    setErr("");
    api
      .get(`/proposed-steps?track=${encodeURIComponent(track)}&county=${encodeURIComponent(county)}`)
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load the recommended steps. Refresh to try again."));
  }, [track, county]);

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    if (k === "track") next.delete("pillar");
    setParams(next, { replace: true });
  };

  const counties = useMemo(() => {
    if (!d) return [];
    return [...d.counties].sort((a, b) => Number(b.priority) - Number(a.priority) || b.population - a.population);
  }, [d]);

  const pillars = useMemo(() => {
    if (!d) return [];
    const counts = new Map();
    d.steps.forEach((s) => {
      if (!s.kpl_pillar_id) return;
      const held = counts.get(s.kpl_pillar_id) || { id: s.kpl_pillar_id, title: s.kpl_pillar, n: 0 };
      counts.set(s.kpl_pillar_id, { ...held, n: held.n + 1 });
    });
    return [...counts.values()];
  }, [d]);

  const steps = useMemo(() => {
    if (!d) return [];
    return pillar === "all" ? d.steps : d.steps.filter((s) => s.kpl_pillar_id === pillar);
  }, [d, pillar]);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="plans-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="plans-loading" />;

  return (
    <div className="bg-black" data-testid="plans-page">
      <StageBar stage="build" />
      <section className="px-5 pb-14 pt-12 md:px-10 md:pt-16">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="eyebrow">Stage two — building the game</p>
            <h1 className="display-1 mt-5 max-w-5xl">
              How To Build Football In One Kenyan County, Step By Step
            </h1>
            <p className="body-copy mt-8 max-w-3xl">
              Three tracks, because they are not the same job.{" "}
              <span className="text-white">The professional game</span> is how one licensed club gets built in one
              county, from incorporation and land to a modular stadium.{" "}
              <span className="text-white">The grassroots game</span> is how the community game in that same county
              gets organised, surfaced, refereed and paid for.{" "}
              <span className="text-white">The national game</span> is the Harambee Stars camp base that sits on top of
              both, so a call-up does not mean leaving your club or your county. Pick a track, pick a county, and every
              step below is priced for that county — its own courts, pitches, academies, teams and stadium, not a
              national average.
            </p>
            <p className="body-copy mt-5 max-w-3xl">
              The order is taken from how professional clubs in the Premier League and the divisions beneath it were
              actually developed — the FA on legal structures, England Football Accreditation, the Football
              Foundation&rsquo;s feasibility-before-funding rule, EFL academy licensing — and then priced with Kenyan
              statutory fees and Kenyan build and wage costs. Statutory fees are published rates. Everything else is
              our estimate, in the open to be argued with.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="steps-track-tabs">
            {d.tracks.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => set("track", t.key)}
                data-testid={`steps-track-${t.key}`}
                className={`border px-5 py-2.5 text-[0.62rem] font-bold uppercase tracking-[0.16em] transition-colors ${
                  d.track === t.key ? "border-ochre bg-ochre/10 text-ochre" : "border-white/12 text-zinc-400 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
            <div className="ml-auto flex flex-wrap items-center gap-3">
              <label htmlFor="steps-county" className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-400">
                County
              </label>
              <select
                id="steps-county"
                value={d.county}
                onChange={(e) => set("county", e.target.value)}
                data-testid="steps-county-select"
                className="border border-white/20 bg-black px-3 py-2 text-sm text-white outline-none focus:border-ochre"
              >
                <optgroup label="Priority 18">
                  {counties.filter((c) => c.priority).map((c) => (
                    <option key={c.county} value={c.county}>{c.county}</option>
                  ))}
                </optgroup>
                <optgroup label="Every other county">
                  {counties.filter((c) => !c.priority).map((c) => (
                    <option key={c.county} value={c.county}>{c.county}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>

          <p className="body-copy mt-8 max-w-3xl" data-testid="steps-standfirst">
            {d.standfirst}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3 text-[0.62rem] uppercase tracking-[0.16em]">
            <span className="border border-maasai px-3 py-1.5 text-maasai" data-testid="steps-county-tag">
              {d.county} · {(d.population / 1e6).toFixed(2)}m people
            </span>
            {d.priority ? (
              <span className="border border-ochre px-3 py-1.5 text-ochre" data-testid="steps-priority-tag">
                Priority 18 · #{d.priority_rank}
              </span>
            ) : (
              <span className="border border-white/15 px-3 py-1.5 text-zinc-400">Outside the Priority 18</span>
            )}
            <Link to={`/counties`} className="text-zinc-400 underline decoration-maasai underline-offset-4 hover:text-white" data-testid="steps-map-link">
              See it on the map
            </Link>
          </div>

          <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="steps-headlines">
            {[
              ["One-off cost, all steps", usd(d.totals.one_off_usd), ksh(d.totals.one_off_ksh)],
              ["Running cost a year", usd(d.totals.annual_usd), ksh(d.totals.annual_ksh)],
              ["Five-year cost", usd(d.totals.five_year_usd), "One-off plus five years of running"],
              [
                "What gets built here",
                `${d.county_facts.five_a_side_courts} court${d.county_facts.five_a_side_courts === 1 ? "" : "s"} · ${d.county_facts.eleven_a_side_pitches} pitch${d.county_facts.eleven_a_side_pitches === 1 ? "" : "es"}`,
                `${d.county_facts.youth_academies} academy · ${d.county_facts.modular_stadiums} stadium · ${d.county_facts.sunday_league_teams.toLocaleString()} teams`,
              ],
            ].map(([label, value, note], i) => (
              <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`steps-headline-${i}`}>
                <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
                <p className="mt-4 font-display text-xl font-bold leading-none text-white md:text-2xl">{value}</p>
                <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
                <AsOf
                  testId={`steps-headline-source-${i}`}
                  kind={i === 3 ? "official" : "estimate"}
                  asOf={d.fx?.as_of}
                  publisher={i === 3 ? "KNBS 2019 census and our county allocation" : "Our estimate, Kenyan cost inputs"}
                  url="/sources"
                />
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="steps-view-tabs">
            {[["steps", "The steps in full"], ["phasing", "Phasing and order"]].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => set("view", k)}
                data-testid={`steps-view-${k}`}
                className={`border px-4 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
                  view === k ? "border-ochre text-ochre" : "border-white/12 text-zinc-500 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {pillars.length ? (
            <div className="mt-8" data-testid="steps-pillar-filter">
              <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">
                Filter by KPL Strategic Plan pillar — see only your own workstream
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {[{ id: "all", title: "Every pillar", n: d.steps.length }, ...pillars].map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => set("pillar", p.id)}
                    data-testid={`steps-pillar-${p.id}`}
                    className={`border px-4 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
                      pillar === p.id ? "border-maasai bg-maasai/10 text-maasai" : "border-white/12 text-zinc-500 hover:text-white"
                    }`}
                  >
                    {p.title} <span className="ml-2 text-zinc-500">{p.n}</span>
                  </button>
                ))}
              </div>
              {pillar !== "all" ? (
                <p className="mono mt-4 text-[0.58rem] uppercase tracking-[0.16em] text-ochre" data-testid="steps-pillar-active">
                  Showing {steps.length} of {d.steps.length} steps · share this view with the link in your address bar
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/apply" className="btn-primary" data-testid="plans-proposal-cta">
              File A County Proposal <ArrowRight size={14} />
            </Link>
            <a
            href={`${API}/steps-pack.pdf?track=${encodeURIComponent(d.track)}&county=${encodeURIComponent(d.county)}`}
            className="btn-ghost"
            data-testid="plans-pack-pdf"
          >
            <Download size={13} /> {d.county} steps pack (PDF)
          </a>
          <Link
            to={`/apply`}
            className="btn-ghost"
            data-testid="plans-prefilled-proposal"
          >
            Propose for {d.county}
          </Link>
          <Link to="/facilities" className="btn-ghost" data-testid="plans-ecosystem-cta">
              The grassroots ecosystem
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          {steps.length === 0 ? (
            <p className="border border-white/10 bg-[#0b0b0b] p-7 text-sm text-zinc-400" data-testid="steps-pillar-empty">
              No step on this track answers that pillar. Pick another pillar or another track.
            </p>
          ) : view === "phasing" ? (
            <Phasing steps={steps} />
          ) : (
            steps.map((s) => <StepBlock key={s.order} step={s} />)
          )}

          <TrackAnchor alignment={d.alignment} />

          <div className="mt-14 border border-white/10 bg-[#0b0b0b] p-7" data-testid="steps-cost-basis">
            <p className="overline">What every cost here is built from</p>
            <p className="mt-2 mono text-[0.6rem] uppercase tracking-[0.18em] text-ochre" data-testid="steps-cost-index">
              {d.county} cost index x{d.cost_index}
            </p>
            <p className="mt-4 max-w-4xl text-sm leading-relaxed text-zinc-400">{d.cost_basis_note}</p>
            <FxStamp fx={d.fx} testId="steps-fx-stamp" />
            <div className="mt-6 overflow-x-auto border border-zinc-900">
              <table className="w-full min-w-[760px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-zinc-900 bg-black/40">
                    {["Input", "Basis", "National rate", `In ${d.county}`, "Published by", "Checked"].map((h) => (
                      <th key={h} className="px-4 py-3 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-zinc-400">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(d.cost_basis || []).map((b, i) => (
                    <tr key={b.input} className="border-b border-zinc-900 align-top" data-testid={`cost-basis-${i}`}>
                      <td className="px-4 py-3 text-sm text-white">{b.input}</td>
                      <td className="px-4 py-3 text-xs leading-relaxed text-zinc-400">{b.basis}</td>
                      <td className="mono px-4 py-3 text-xs text-zinc-300">
                        {b.value}
                        {b.unit && b.unit !== "market" ? (
                          <span className="mt-1 block text-[0.55rem] uppercase tracking-[0.14em] text-zinc-600">
                            {b.unit}
                          </span>
                        ) : null}
                      </td>
                      <td className="mono px-4 py-3 text-xs text-ochre" data-testid={`cost-basis-county-${i}`}>
                        {b.local ? b.county_value : "National rate"}
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-300">
                        <a
                          href={b.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 hover:text-ochre"
                        >
                          {b.publisher} <ExternalLink size={10} />
                        </a>
                        <span className="mt-2 block">
                          <VerifyChip v={b.verification} meaning={b.verification_meaning} testId={`cost-basis-label-${i}`} />
                        </span>
                        {b.published ? (
                          <span className="mono mt-2 block text-[0.5rem] uppercase tracking-[0.14em] text-zinc-600">
                            published {b.published}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-[0.58rem] uppercase tracking-[0.14em] text-zinc-500">
                        {b.kind} · {b.cadence}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Link
              to="/sources"
              className="mono mt-5 inline-block text-[0.6rem] uppercase tracking-[0.16em] text-ochre underline underline-offset-4"
              data-testid="steps-sources-link"
            >
              Every source on this site, and when it was last read
            </Link>
          </div>

          <div className="mt-14 border border-white/10 bg-[#0b0b0b] p-7" data-testid="steps-sources">
            <p className="overline">How the steps and the prices were set</p>            <p className="mt-4 max-w-4xl text-sm leading-relaxed text-zinc-400">{d.note}</p>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-4">
              {d.sources.map((s, i) => (
                <div key={s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`steps-source-${i}`}
                    className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.14em] text-zinc-300 hover:text-ochre"
                  >
                    {s.publisher} — {s.label} <ExternalLink size={11} />
                  </a>
                  <span className="mt-2 block">
                    <VerifyChip v={s.verification} meaning={s.verification_meaning} testId={`steps-source-label-${i}`} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900 bg-[#0b0b0b] py-16">
        <div className="mx-auto flex max-w-[1500px] flex-col items-start justify-between gap-8 px-5 md:flex-row md:items-center md:px-10">
          <div className="max-w-xl">
            <p className="eyebrow">The commercial case</p>
            <h2 className="display-3 mt-4">Can you deliver any of this better?</h2>
            <p className="body-copy mt-5">
              We would rather back the right partner than build everything ourselves. Submissions are open to
              the diaspora, county governments, clubs, schools, contractors and coach educators — including ones
              that re-scope or re-sequence these steps entirely.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/apply" className="btn-primary" data-testid="plans-support-cta">
              Tell us what you could deliver <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="btn-ghost" data-testid="plans-contact-cta">
              Talk to us first
            </Link>
          </div>
        </div>
      </section>
      <KplAlignment />
      <StageClose stage="build" />
    </div>
  );
}
